import React from 'react'
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from '@mui/material'
import type { PreviewRow } from '@app/src/types/tabularImport'

type MappingPreviewTableProps = {
  previewRows: PreviewRow[]
  activeFields: string[]
  disabledFields: string[]
  totalRows: number
  categoryColumns?: string[]
}

// Preview row keys are camelCase (JSON convention), field names are PascalCase (C# convention)
const toCamelCase = (s: string) => s.charAt(0).toLowerCase() + s.slice(1)

const MappingPreviewTable: React.FC<MappingPreviewTableProps> = ({
  previewRows,
  activeFields,
  disabledFields,
  totalRows,
  categoryColumns,
}) => {
  const allColumns = [...activeFields, ...disabledFields]
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {allColumns.map(field => (
              <TableCell
                key={field}
                sx={{
                  fontWeight: 'bold',
                  opacity: disabledFields.includes(field) ? 0.4 : 1,
                  backgroundColor: disabledFields.includes(field) ? 'action.hover' : undefined,
                }}
              >
                {field}
              </TableCell>
            ))}
            {categoryColumns?.map(col => (
              <TableCell
                key={`cat-${col}`}
                sx={{ backgroundColor: 'info.main', color: 'info.contrastText', fontWeight: 500 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label="CAT" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
                  {col}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {previewRows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {allColumns.map(field => (
                <TableCell
                  key={field}
                  sx={{
                    opacity: disabledFields.includes(field) ? 0.4 : 1,
                    backgroundColor: disabledFields.includes(field) ? 'action.hover' : undefined,
                  }}
                >
                  {row[toCamelCase(field)] ?? '\u2014'}
                </TableCell>
              ))}
              {categoryColumns?.map(col => (
                <TableCell key={`cat-${col}`} sx={{ backgroundColor: 'action.hover' }}>
                  {row[toCamelCase(col)] ?? '\u2014'}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography variant="caption" sx={{ p: 1, display: 'block', textAlign: 'right' }}>
        Showing {previewRows.length} of {totalRows} rows
      </Typography>
    </TableContainer>
  )
}

export default MappingPreviewTable
